// "Scan storefront colors" - find the literal colour values a storefront has accumulated and
// rewrite them as `$palette:<slot>` tokens, so the palette becomes the single place they are set.
//
// Three write paths, each riding the mechanism that already owns that data:
//
//   1. the storefront's typed style fields   -> styleConfig (setStyle), a working-settings draft
//                                               edit that persists on Exit Edit Mode
//   2. the page-config blobs                 -> the same settings object, rewritten in place -
//                                               exactly the channel "Apply to all" uses
//                                               (styleBulkApply.applyStyleToAll)
//   3. the named Storefront Styles           -> a save through the styles API, one doc per change
//
// Section StyleProps INSTANCES are deliberately out of scope, for the same reason "Apply to all"
// leaves them alone: those are per-section decisions with their own editor, and they are raw CSS
// dictionaries rather than the resolver-backed dicts the tokens are safe in. The dialog says so.
//
// The palette and font-pack docs themselves are never rewritten - tokenizing a palette's own
// colours would make it reference itself.

import { IStorefrontSettings, IStorefrontStyle } from '../../../models/Storefront';
import { isTokenElementType, PALETTE_TOKEN_PREFIX, PaletteSlot } from './stylesConfig';
import { STYLE_CONFIG_TARGETS } from './styleBulkApply';
import { parseStyleConfig, serializeStyleConfig, STYLE_KEYS, StyleKey, styleValue } from './styleConfig';

/** The typed settings fields that hold a COLOUR (the two font keys are not scanned). */
export const SCANNED_COLOR_SETTINGS_KEYS: StyleKey[] = STYLE_KEYS.filter(
    (key) => key !== 'headerFont' && key !== 'defaultFont');

// ── Colour parsing ───────────────────────────────────────────────────────────────────────
// Only the two forms the storefront editors actually produce: #rgb / #rrggbb from the colour
// picker, and rgb()/rgba() from the same picker with its alpha slider on. Anything else (named
// colours, gradients, 8-digit hex, CSS vars) is left alone rather than half-understood.

const HEX_PATTERN = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const RGB_PATTERN = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(\d*\.?\d+)\s*)?\)$/i;

interface IParsedColor {
    /** `#rrggbb` (lowercase) - the colour with its alpha removed. */
    hex: string;
    /** 0..1, rounded to two places. 1 for an opaque colour. */
    alpha: number;
}

const toHex = (r: number, g: number, b: number): string =>
    `#${[r, g, b].map((n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')).join('')}`;

/** Parse a style value as a colour, or undefined when it is not one of the supported forms. */
export function parseColorValue(value: unknown): IParsedColor | undefined {
    if (typeof value !== 'string') return undefined;
    const text = value.trim();
    if (!text) return undefined;
    const hex = text.match(HEX_PATTERN);
    if (hex) {
        const digits = hex[1].length === 3
            ? `${hex[1][0]}${hex[1][0]}${hex[1][1]}${hex[1][1]}${hex[1][2]}${hex[1][2]}`
            : hex[1];
        return { hex: `#${digits.toLowerCase()}`, alpha: 1 };
    }
    const rgb = text.match(RGB_PATTERN);
    if (rgb) {
        const alpha = rgb[4] === undefined ? 1 : Math.round(Number(rgb[4]) * 100) / 100;
        if (!isFinite(alpha) || alpha < 0 || alpha > 1) return undefined;
        return { hex: toHex(Number(rgb[1]), Number(rgb[2]), Number(rgb[3])), alpha };
    }
    return undefined;
}

/**
 * The grouping key for a parsed colour. Opaque colours group by hex alone, so `#000000`,
 * `#000` and `rgb(0,0,0)` are one entry; a translucent colour keeps its alpha, because mapping it
 * has to emit a different token.
 */
export const colorKey = (color: IParsedColor): string =>
    color.alpha === 1 ? color.hex : `${color.hex}@${color.alpha}`;

/** The token that replaces a colour mapped to `slot`, preserving any alpha. */
export const tokenFor = (slot: PaletteSlot, alpha: number): string =>
    alpha === 1 ? `${PALETTE_TOKEN_PREFIX}${slot}` : `${PALETTE_TOKEN_PREFIX}${slot}@${alpha}`;

// ── Scan ─────────────────────────────────────────────────────────────────────────────────

export interface IColorUsage {
    /** The grouping key - also the key of the mapping object the caller builds. */
    key: string;
    /** `#rrggbb`, for the swatch. */
    hex: string;
    alpha: number;
    /** How many places carry this colour. */
    count: number;
    /** Where it was found ("Storefront colors", "Home page", "Named styles"), for the dialog. */
    sources: string[];
}

class UsageCollector {
    private readonly map = new Map<string, IColorUsage>();

    add(value: unknown, source: string): void {
        const parsed = parseColorValue(value);
        if (!parsed) return;
        const key = colorKey(parsed);
        const existing = this.map.get(key);
        if (existing) {
            existing.count++;
            if (existing.sources.indexOf(source) < 0) existing.sources.push(source);
            return;
        }
        this.map.set(key, { key, hex: parsed.hex, alpha: parsed.alpha, count: 1, sources: [source] });
    }

    /** Most-used first, then darkest-first so the list is stable between runs. */
    result(): IColorUsage[] {
        return [...this.map.values()].sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
    }
}

/** Walk any parsed-JSON node, handing every string value to `visit`. */
function walkStrings(node: unknown, visit: (value: string) => void): void {
    if (typeof node === 'string') { visit(node); return; }
    if (!node || typeof node !== 'object') return;
    if (Array.isArray(node)) { for (const item of node) walkStrings(item, visit); return; }
    for (const value of Object.values(node as Record<string, unknown>)) walkStrings(value, visit);
}

function parseConfigBlob(raw?: string): Record<string, unknown> | undefined {
    if (!raw) return undefined;
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;
        return parsed as Record<string, unknown>;
    } catch {
        // Hand-edited raw JSON that no longer parses - never scanned, never rewritten.
        return undefined;
    }
}

/**
 * Every literal colour on the storefront, grouped by distinct colour with a usage count. Scans the
 * typed style fields, the page-config blobs and the named styles (excluding the palette/font-pack
 * docs themselves).
 */
export function scanStorefrontColors(
    settings: IStorefrontSettings | undefined,
    styles: IStorefrontStyle[] | undefined,
): IColorUsage[] {
    const collector = new UsageCollector();

    if (settings) {
        for (const key of SCANNED_COLOR_SETTINGS_KEYS) collector.add(styleValue(settings, key), 'Storefront colors');
        for (const target of STYLE_CONFIG_TARGETS) {
            const config = parseConfigBlob(settings[target.configField]);
            if (config) walkStrings(config, (value) => collector.add(value, target.label));
        }
    }

    for (const style of styles ?? []) {
        if (isTokenElementType(style.elementType)) continue;
        walkStrings(style.parts ?? {}, (value) => collector.add(value, 'Named styles'));
    }

    return collector.result();
}

// ── Apply ────────────────────────────────────────────────────────────────────────────────

/** colour key (see colorKey) -> the palette slot it becomes. Absent = skip. */
export type IColorTokenMapping = Record<string, PaletteSlot | undefined>;

const tokenForValue = (value: unknown, mapping: IColorTokenMapping): string | undefined => {
    const parsed = parseColorValue(value);
    if (!parsed) return undefined;
    const slot = mapping[colorKey(parsed)];
    return slot ? tokenFor(slot, parsed.alpha) : undefined;
};

/** Rewrite every mapped colour string inside a parsed-JSON node. Returns the new node + a count. */
function rewriteStrings(node: unknown, mapping: IColorTokenMapping, counter: { count: number }): unknown {
    if (typeof node === 'string') {
        const token = tokenForValue(node, mapping);
        if (token === undefined) return node;
        counter.count++;
        return token;
    }
    if (!node || typeof node !== 'object') return node;
    if (Array.isArray(node)) return node.map((item) => rewriteStrings(item, mapping, counter));
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
        out[key] = rewriteStrings(value, mapping, counter);
    }
    return out;
}

export interface ITokenizeResult {
    /** The working settings with the typed fields + page-config blobs rewritten. */
    settings: IStorefrontSettings | undefined;
    /** The named styles that actually changed - save these through the styles API. */
    changedStyles: IStorefrontStyle[];
    settingsFields: number;
    configValues: number;
    styleValues: number;
    /** settingsFields + configValues + styleValues - the headline number for the result toast. */
    total: number;
}

/**
 * Apply a colour -> slot mapping across all three write paths. Pure: it returns the new settings
 * object and the list of changed styles; performing the saves is the caller's job (so each one
 * rides its own existing save mechanism).
 */
export function applyColorTokens(
    settings: IStorefrontSettings | undefined,
    styles: IStorefrontStyle[] | undefined,
    mapping: IColorTokenMapping,
): ITokenizeResult {
    let settingsFields = 0;
    const configCounter = { count: 0 };
    let nextSettings = settings;

    if (settings) {
        // 1. Typed style fields, through styleConfig - a working-settings draft edit.
        const config = parseStyleConfig(settings.styleConfig);
        let styleConfigChanged = false;
        for (const key of SCANNED_COLOR_SETTINGS_KEYS) {
            const token = tokenForValue(styleValue(settings, key), mapping);
            if (token === undefined) continue;
            config[key] = token;
            styleConfigChanged = true;
            settingsFields++;
        }

        // 2. The page-config blobs, on the same settings object.
        const patched: IStorefrontSettings = { ...settings };
        if (styleConfigChanged) patched.styleConfig = serializeStyleConfig(config);
        for (const target of STYLE_CONFIG_TARGETS) {
            const parsed = parseConfigBlob(settings[target.configField]);
            if (!parsed) continue;
            const before = configCounter.count;
            const rewritten = rewriteStrings(parsed, mapping, configCounter);
            if (configCounter.count > before) patched[target.configField] = JSON.stringify(rewritten);
        }
        nextSettings = (styleConfigChanged || configCounter.count > 0) ? patched : settings;
    }

    // 3. The named styles - one save per changed doc.
    const changedStyles: IStorefrontStyle[] = [];
    const styleCounter = { count: 0 };
    for (const style of styles ?? []) {
        if (isTokenElementType(style.elementType)) continue;
        const before = styleCounter.count;
        const parts = rewriteStrings(style.parts ?? {}, mapping, styleCounter);
        if (styleCounter.count > before) changedStyles.push({ ...style, parts: parts as IStorefrontStyle['parts'] });
    }

    return {
        settings: nextSettings,
        changedStyles,
        settingsFields,
        configValues: configCounter.count,
        styleValues: styleCounter.count,
        total: settingsFields + configCounter.count + styleCounter.count,
    };
}
